import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";

/* istanbul ignore next */
const PdfEditor = (props) => {
    const { isPopup, handleUploadPdfAction } = props;
    const fileInputRef = useRef(null);
    const canvasRef = useRef(null);
    const ctxRef = useRef(null);
    const [state, setState] = useState({
        pdfDoc: null,
        pdfDataURL: null,
        currentPage: 1,
        totalPages: 0,
        scale: 1.5,
        rotation: {}, // per-page rotation
        isAddingText: false,
        textElements: {},
        originalFileName: "",
        pages: [] // mapping of visible pages to original PDF page numbers
    });

    const textInputRef = useRef(null);
    const fontSizeRef = useRef(null);
    const textColorRef = useRef(null);

    const [showTextControls, setShowTextControls] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("Your PDF will load here.");

    useEffect(() => {
        ctxRef.current = canvasRef.current.getContext("2d");
        window.pdfjsLib.GlobalWorkerOptions.workerSrc =
            "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.worker.min.js";
    }, []);

    const handleCancelPdf = () => {
        setState({
            pdfDoc: null,
            pdfDataURL: null,
            currentPage: 1,
            totalPages: 0,
            scale: 1.5,
            rotation: {},
            isAddingText: false,
            textElements: {},
            originalFileName: "",
            pages: []
        });
        setLoadingMessage("Your PDF will load here.");
        setShowTextControls(false);
        if (fileInputRef.current) fileInputRef.current.value = null;
        const ctx = canvasRef.current.getContext("2d");
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        canvasRef.current.width = 0;
    };

    const loadPDF = async (file) => {
        const dataURL = await readFileAsDataURL(file);
        const objectUrl = URL.createObjectURL(file);
        const pdf = await window.pdfjsLib.getDocument(objectUrl).promise;
        const newTextElements = {};
        const newRotations = {};
        const pages = [];
        for (let i = 1; i <= pdf.numPages; i++) {
            newTextElements[i] = [];
            newRotations[i] = 0;
            pages.push(i);
        }
        setState((prev) => ({
            ...prev,
            pdfDoc: pdf,
            pdfDataURL: dataURL,
            totalPages: pdf.numPages,
            currentPage: 1,
            textElements: newTextElements,
            rotation: newRotations,
            originalFileName: file.name,
            pages
        }));
        await renderPage(pdf, 1, newTextElements, newRotations[pages[0]], pages);
    };

    // Updated renderPage to accept an explicit pages array (pagesArg) to avoid relying on possibly-stale state
    const renderPage = async (pdfDoc, pageIndex, elements = state.textElements, rotationForActualPage = undefined, pagesArg = null) => {
        // pageIndex is 1-based index in the current (possibly reduced) pages array
        if (!pdfDoc) return;

        let pagesToUse;
        if (pagesArg?.length) {
            pagesToUse = pagesArg;
        } else if (state?.pages?.length) {
            pagesToUse = state.pages;
        }

        const actualPageNum = pagesToUse ? pagesToUse[pageIndex - 1] : pageIndex;
        setLoadingMessage(`Rendering page ${pageIndex}...`);
        const page = await pdfDoc.getPage(actualPageNum);
        const rotation = rotationForActualPage !== undefined ? rotationForActualPage : (state.rotation[actualPageNum] || 0);
        const viewport = page.getViewport({ scale: state.scale, rotation });
        const canvas = canvasRef.current;
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        await page.render({ canvasContext: ctxRef.current, viewport }).promise;
        drawTextElements(elements, actualPageNum);
        setLoadingMessage("");
    };

    const drawTextElements = (elements, pageNum) => {
        const ctx = ctxRef.current;
        if (!elements[pageNum]) return;
        elements[pageNum].forEach((el) => {
            ctx.font = `${el.fontSize}px Arial`;
            ctx.fillStyle = el.color;
            const lines = el.text.split("\n");
            lines.forEach((line, index) => {
                const lineY = el.y + index * el.fontSize * 1.2;
                ctx.fillText(line, el.x, lineY);
            });
        });
    };

    const addTextAtPosition = (x, y) => {
        const text = textInputRef.current.value.trim();
        if (!text) return;
        const newText = {
            text,
            x,
            y,
            fontSize: parseInt(fontSizeRef.current.value),
            color: textColorRef.current.value,
            page: state.currentPage
        };
        const updatedElements = { ...state.textElements };
        updatedElements[state.currentPage] = [...(updatedElements[state.currentPage] || []), newText];
        setState((prev) => ({ ...prev, textElements: updatedElements }));
        renderPage(state.pdfDoc, state.currentPage, updatedElements, undefined, state.pages);
    };

    const readFileAsDataURL = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });

    const savePDF = async (uploadPdf = false) => {
        setLoadingMessage("Generating PDF...");
        // Load original PDF and create a new PDF that only contains the remaining pages in order
        const existingPdfBytes = await fetch(state.pdfDataURL).then(res => res.arrayBuffer());
        const origPdf = await window.PDFLib.PDFDocument.load(existingPdfBytes);
        const newPdf = await window.PDFLib.PDFDocument.create();
        // copy pages in the current order represented by state.pages
        const indices = (state.pages || []).map(p => p - 1);
        const copiedPages = await newPdf.copyPages(origPdf, indices);
        copiedPages.forEach((p) => newPdf.addPage(p));
        const helveticaFont = await newPdf.embedFont(window.PDFLib.StandardFonts.Helvetica);

        // Draw text and apply rotations on the new document pages. The mapping from new page index to original page number:
        for (let i = 0; i < (state.pages || []).length; i++) {
            const origPageNum = state.pages[i];
            const page = newPdf.getPage(i);
            const rot = state.rotation[origPageNum] || 0;
            page.setRotation(window.PDFLib.degrees(rot));
            (state.textElements[origPageNum] || []).forEach((el) => {
                const [r, g, b] = el.color.substring(1).match(/.{2}/g).map((hex) => parseInt(hex, 16) / 255);
                page.drawText(el.text, {
                    x: el.x / state.scale,
                    y: page.getHeight() - el.y / state.scale,
                    size: el.fontSize,
                    font: helveticaFont,
                    color: window.PDFLib.rgb(r, g, b)
                });
            });
        }

        const pdfBytes = await newPdf.save();
        const blob = new Blob([pdfBytes], { type: "application/pdf" });
        setLoadingMessage("");
        if (uploadPdf) {
            window.saveAs(blob, state.originalFileName.replace(".pdf", "_edited.pdf"));
            handleUploadPdfAction(blob, state);
            return;
        }
        window.saveAs(blob, state.originalFileName.replace(".pdf", "_edited.pdf"));
    };

    const resetPDF = async () => {
        if (!state.pdfDataURL) return;
        const response = await fetch(state.pdfDataURL);
        const fileBlob = await response.blob();
        const file = new File([fileBlob], state.originalFileName, { type: "application/pdf" });
        await loadPDF(file);
    };

    const rotateCurrentPage = () => {
        setState((prev) => {
            const newRotations = { ...prev.rotation };
            const actualPageNum = (prev?.pages?.length) ? prev.pages[prev.currentPage - 1] : prev.currentPage;
            newRotations[actualPageNum] = ((newRotations[actualPageNum] || 0) + 90) % 360;
            renderPage(prev.pdfDoc, prev.currentPage, prev.textElements, newRotations[actualPageNum], prev.pages);
            return { ...prev, rotation: newRotations };
        });
    };

    const deleteCurrentPage = async () => {
        if (!state.pdfDoc) return;
        if (!window.confirm || !window.confirm('Delete current page?')) return;
        setState((prev) => {
            const pagesArr = [...(prev.pages || [])];
            const pageIndex = prev.currentPage - 1; // 0-based index in pages array
            // validate index
            if (pageIndex < 0 || pageIndex >= pagesArr.length) {
                // invalid index, do nothing
                return prev;
            }
            const newPages = pagesArr;
            const removedOrigPage = newPages.splice(pageIndex, 1)[0];
            // remove textElements and rotation for removed original page
            const newTextElements = { ...prev.textElements };
            const newRotations = { ...prev.rotation };
            delete newTextElements[removedOrigPage];
            delete newRotations[removedOrigPage];

            const newTotal = newPages.length;
            if (newTotal === 0) {
                // nothing left, clear everything
                setTimeout(() => handleCancelPdf(), 0);
                return { ...prev, pages: [], totalPages: 0, currentPage: 1, textElements: newTextElements, rotation: newRotations };
            }
            const newCurrent = Math.min(prev.currentPage, newTotal);
            // re-render the new current page after state update, pass the updated pages so renderPage doesn't rely on stale state
            setTimeout(() => renderPage(prev.pdfDoc, newCurrent, newTextElements, undefined, newPages), 0);
            return { ...prev, pages: newPages, totalPages: newTotal, currentPage: newCurrent, textElements: newTextElements, rotation: newRotations };
        });
    };

    return (
        <main className={`${isPopup ? "pdfEditorPopup" : ""}`}>
            <div className={`${isPopup ? "" : "main__container"}`}>
                {!isPopup && (
                    <div className="main__tile_heading cus-btn-include-sec mb-0 mb-3">
                        <span>PDF Editor</span>
                    </div>
                )}
                <div className="cus-page-content-sec">
                    <div className="content-container pdfEditor">
                        <div className="controls">
                            <button className="btn-input-csv">
                                <label className="btn-chooseFile" htmlFor={`loadPdfBtn`}>Select File</label>
                            </button>
                            <input
                                type="file"
                                accept=".pdf"
                                ref={fileInputRef}
                                onChange={(e) => loadPDF(e.target.files[0])}
                                id="loadPdfBtn"
                                hidden
                            />
                            <div className="file-name">
                                {isPopup && (
                                    <button
                                        onClick={() => savePDF(true)}
                                        className="btn cus-seconday-bg-btn top-btn"
                                        disabled={!state.pdfDoc}
                                    >
                                        Upload
                                    </button>
                                )}
                                <button
                                    onClick={() => { setState((p) => ({ ...p, isAddingText: true })); setShowTextControls(true); }}
                                    className="btn cus-seconday-bg-btn top-btn"
                                    disabled={!state.pdfDoc}
                                >
                                    Add Text
                                </button>

                                {/* NEW Rotate Button */}
                                <button
                                    onClick={rotateCurrentPage}
                                    className="btn cus-seconday-bg-btn top-btn"
                                    disabled={!state.pdfDoc}
                                >
                                    Rotate
                                </button>

                                <button
                                    onClick={deleteCurrentPage}
                                    className="btn cus-seconday-bg-btn top-btn"
                                    disabled={!state.pdfDoc}
                                >
                                    Delete Page
                                </button>

                                <button
                                    onClick={savePDF}
                                    disabled={!state.pdfDoc}
                                    className="btn cus-seconday-bg-btn top-btn"
                                >
                                    Download PDF
                                </button>
                                <button
                                    onClick={handleCancelPdf}
                                    disabled={!state.pdfDoc}
                                    className="btn cus-seconday-bg-btn"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>

                        <div className="editControls mt-3">
                            <div className="text-controls">
                                {showTextControls && (
                                    <>
                                        <textarea type="text" placeholder="Enter text" ref={textInputRef} className="form-control" />

                                        <select ref={fontSizeRef} defaultValue="10" className="form-control fntSize">
                                            <option value="10">10pt</option>
                                            <option value="12">12pt</option>
                                            <option value="14">14pt</option>
                                            <option value="18">18pt</option>
                                            <option value="24">24pt</option>
                                            <option value="36">36pt</option>
                                        </select>
                                        <input type="color" defaultValue="#000000" className="form-control fntSize" ref={textColorRef} />
                                        <button
                                            onClick={() => {
                                                const x = canvasRef.current.width / 2;
                                                const y = canvasRef.current.height / 2;
                                                addTextAtPosition(x, y);
                                                setState((prev) => ({ ...prev, isAddingText: false }));
                                            }}
                                            className="btn cus-seconday-bg-btn top-btn"
                                        >
                                            Apply
                                        </button>
                                        <button
                                            onClick={() => {
                                                resetPDF();
                                            }}
                                            className="btn cus-seconday-bg-btn"
                                        >
                                            Reset
                                        </button>
                                    </>
                                )}
                            </div>

                            <div className="page-controls mt-3">
                                {state.pdfDoc && (
                                    <div className="pagination">
                                        <button
                                            id="prev-page"
                                            onClick={() => {
                                                if (state.currentPage > 1) {
                                                    const newPage = state.currentPage - 1;
                                                    setState((prev) => ({ ...prev, currentPage: newPage }));
                                                    renderPage(state.pdfDoc, newPage, undefined, undefined, state.pages);
                                                }
                                            }}
                                            className="prevPageBtn"
                                        >
                                            <i className="fa fa-chevron-left" aria-hidden="true"></i>
                                        </button>
                                        <span id="page-info" className="ml-2 mr-2">Page: {state.currentPage} of {state.totalPages}</span>
                                        <button
                                            id="next-page"
                                            onClick={() => {
                                                if (state.currentPage < state.totalPages) {
                                                    const newPage = state.currentPage + 1;
                                                    setState((prev) => ({ ...prev, currentPage: newPage }));
                                                    renderPage(state.pdfDoc, newPage, undefined, undefined, state.pages);
                                                }
                                            }}
                                            className="prevPageBtn"
                                        >
                                            <i className="fa fa-chevron-right" aria-hidden="true"></i>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div id="pdf-container" style={{ position: "relative" }}>
                            {loadingMessage && <div id="loading" className="loadingText">{loadingMessage}</div>}
                            <canvas id="pdf-canvas" ref={canvasRef} onClick={(e) => {
                                if (state.isAddingText) {
                                    const rect = canvasRef.current.getBoundingClientRect();
                                    const x = e.clientX - rect.left;
                                    const y = e.clientY - rect.top;
                                    addTextAtPosition(x, y);
                                    textInputRef.current.value = ""; // Clear the input after adding text
                                }
                            }}></canvas>
                        </div>
                    </div>
                </div>
            </div>
        </main>

    );
};

PdfEditor.propTypes = {
    isPopup: PropTypes.bool,
    handleUploadPdfAction: PropTypes.func,
}

export default PdfEditor;